<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { DefectTypes, DefectStatusTypes, DefectStatusValueTypes } from 'Modules/types/pages/imageReview/enums'
import { ImageAnnotationType } from '@/modules/types/pages/imageReview/imageAnnotationType'
import useTailwindColor from 'Common/utils/useTailwindColor'
import { JeteyeRoutes } from 'Modules/RouterSetup'

interface PropTypes {
  annotations: Array<ImageAnnotationType>
}

const props = defineProps<PropTypes>()
const router = useRouter()
const route = useRoute()

const findingsData = computed(() => {
  if (!props.annotations) return []
  return props.annotations.map((annotation) => ({
    ...annotation,
    status: DefectStatusTypes[annotation.defectStatus],
    type: DefectTypes[annotation.defectType],
  }))
})

const tableRowClassName = ({ row }) => {
  const baseClass = 'discpepancy-findings'
  if (row?.defectStatus === DefectStatusValueTypes.GroundingCondition) {
    return `${baseClass} warning-row`
  }
  return baseClass
}

const handleRowClick = (row) => {
  //TODO remove inspection instance condition once summary and review page are opened up to broader filter options.
  if (row?.imageUuid && route.query.inspectionInstance)
    router.push({
      name: JeteyeRoutes.ImageReview,
      query: { inspectionInstance: route.query.inspectionInstance, inspectionImage: [row?.imageUuid] },
    })
  else console.error("Image and Inspection Instance UUID's are not defined and are required to build review page url.")
}

const warningRowColor = useTailwindColor('red-50')
</script>

<template>
  <div id="summary-findings" class="pb-48">
    <el-table
      :data="findingsData"
      :default-sort="{ prop: 'status', order: 'ascending' }"
      :border="false"
      height="100%"
      size="large"
      style="width: 100%"
      :row-class-name="tableRowClassName"
      @row-click="handleRowClick"
    >
      <el-table-column prop="identifier" label="ID" width="80" />
      <el-table-column sortable prop="type" label="Type">
        <template #default="scope">
          <span v-if="scope.row?.type">
            {{ scope.row.type }}
          </span>
          <span v-else class="text-gray-300">No Type </span>
        </template>
      </el-table-column>
      <el-table-column sortable prop="status" label="Severity">
        <template #default="scope">
          <span v-if="scope.row?.status">
            {{ scope.row.status }}
          </span>
          <span v-else class="text-gray-300">No Severity </span>
        </template>
      </el-table-column>
      <el-table-column sortable prop="defectComment" label="Comments">
        <template #default="scope">
          <span v-if="scope.row?.defectComment">
            {{ scope.row.defectComment }}
          </span>
          <span v-else class="text-gray-300">No Comment </span>
        </template>
      </el-table-column>
      <el-table-column sortable prop="workcardTitle" label="Work Card">
        <template #default="scope">
          <span v-if="scope.row?.workcardTitle">
            {{ scope.row.workcardTitle }}
          </span>
          <span v-else class="text-gray-300">No Workcard Specified</span>
        </template>
      </el-table-column>
      <el-table-column sortable prop="waypointLocation" label="Location(s)" :show-overflow-tooltip="true">
        <template #default="scope">
          <span v-if="scope.row?.generalLocations.length > 0">
            {{ scope.row.generalLocations.join(', ') }}
          </span>
          <span v-else class="text-gray-300">No general location(s) specified</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style>
.el-table tr.warning-row {
  background-color: v-bind(warningRowColor);
}

.el-table tr.discpepancy-findings:hover {
  cursor: pointer;
}
</style>
